import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, FileSpreadsheet, MoreVertical, Pencil, Trash2, Eye, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatDateShort, formatApiError, STATUS_LABELS } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import DataPagination from "@/components/DataPagination";
import StatusBadge from "@/components/StatusBadge";
import { TableSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUSES = ["draft", "pending", "approved", "rejected"];

const Quotations = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotations", { params: { search, status, page, limit: 8 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const convert = async (id) => {
    try {
      const res = await api.post(`/quotations/${id}/convert`);
      toast.success("Berhasil dikonversi ke invoice");
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/quotations/${delId}`);
      toast.success("Penawaran dihapus");
      load();
    } finally {
      setDelId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Penawaran" subtitle="Buat penawaran harga dan konversi ke invoice">
        <Button onClick={() => navigate("/quotations/new")} data-testid="add-quotation-btn">
          <Plus className="mr-2 h-4 w-4" /> Buat Penawaran
        </Button>
      </PageHeader>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari no. / customer..." className="pl-9" value={search} data-testid="quotation-search-input" onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
        {loading ? (
          <div className="p-4"><TableSkeleton cols={6} /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={FileSpreadsheet} title="Belum ada penawaran" description="Buat penawaran harga untuk calon pelanggan." action={<Button onClick={() => navigate("/quotations/new")}><Plus className="mr-2 h-4 w-4" />Buat Penawaran</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Penawaran</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((q) => (
                <TableRow key={q.id} data-testid={`quotation-row-${q.id}`} className="cursor-pointer" onClick={() => navigate(`/quotations/${q.id}`)}>
                  <TableCell className="font-mono text-xs font-medium text-primary">{q.number}</TableCell>
                  <TableCell>{q.customer_name}</TableCell>
                  <TableCell>{formatDateShort(q.date)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatRupiah(q.grand_total)}</TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`quotation-actions-${q.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/quotations/${q.id}`)}><Eye className="mr-2 h-4 w-4" />Lihat</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/quotations/${q.id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => convert(q.id)} disabled={!!q.converted_invoice_id}><ArrowRightLeft className="mr-2 h-4 w-4" />Konversi ke Invoice</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDelId(q.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Hapus</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && <DataPagination page={page} limit={8} total={data.total} onPage={setPage} />}
      <ConfirmDialog open={!!delId} onOpenChange={() => setDelId(null)} title="Hapus Penawaran?" description="Tindakan ini tidak dapat dibatalkan." onConfirm={doDelete} />
    </div>
  );
};

export default Quotations;
